import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '@env';
import { PaginaRespuestaDto } from '@nucleo/http/pagina-respuesta-dto';
import { Permiso } from '@nucleo/seguridad/permiso.enum';
import { SesionServicio } from '@nucleo/sesion/sesion-servicio';

/** Aviso de la bandeja propia del usuario (`GET /me/notifications` del servicio de comunicación). */
export interface AvisoPropioDto {
  id: string;
  title: string;
  body: string;
  link?: string | null;
  readAt?: string | null;
  createdAt?: string | null;
}

const RUTA_AVISOS = '/me/notifications';

/**
 * Avisos que la administración o el sistema dejan al usuario (pago aprobado o rechazado, paquete recibido...). Alimenta la
 * campana de la barra superior: cuántos hay sin leer y la lista para leerlos. Solo se consulta si el usuario puede ver avisos.
 */
@Injectable({ providedIn: 'root' })
export class AvisosPropiosServicio {
  private readonly http = inject(HttpClient);
  private readonly sesion = inject(SesionServicio);
  private readonly url = `${environment.apisBase.comunicacion.url}${RUTA_AVISOS}`;

  readonly avisos = signal<AvisoPropioDto[]>([]);
  readonly sinLeer = computed(() => this.avisos().filter((aviso) => !aviso.readAt).length);
  /** Avisos de pago que los propietarios informaron y administración todavía no revisó. */
  readonly pagosPendientes = signal(0);
  readonly puedeVerAvisos = computed(() => this.sesion.tienePermiso(Permiso.COMUNICACION_NOTIFICACIONES_CONSULTAR));
  readonly puedeRevisarPagos = computed(() => this.sesion.tienePermiso(Permiso.FINANZAS_AVISOS_PAGO_ADMINISTRAR));
  /** La campana se muestra si hay algo que mirar: avisos propios o pagos por revisar. */
  readonly disponible = computed(() => this.puedeVerAvisos() || this.puedeRevisarPagos());
  /** Lo que llama la atención en el globo: avisos sin leer más pagos por revisar. */
  readonly total = computed(() => this.sinLeer() + this.pagosPendientes());

  /** Vuelve a pedir los avisos; si falla (servicio apagado) deja la lista como estaba. */
  cargar(): void {
    if (this.puedeVerAvisos()) {
      this.http.get<AvisoPropioDto[] | { content: AvisoPropioDto[] }>(this.url).subscribe({
        next: (respuesta) => this.avisos.set(Array.isArray(respuesta) ? respuesta : (respuesta.content ?? [])),
        error: () => undefined,
      });
    }
    if (this.puedeRevisarPagos()) {
      this.http
        .get<PaginaRespuestaDto<unknown>>(`${environment.apisBase.finanzas.url}/payment-reports`, {
          params: { reportStatus: 'PENDING', size: 1 },
        })
        .subscribe({ next: (pagina) => this.pagosPendientes.set(pagina.totalElements ?? 0), error: () => undefined });
    }
  }

  marcarLeido(aviso: AvisoPropioDto): void {
    if (aviso.readAt) return;
    this.http.post(`${this.url}/${aviso.id}/read`, {}).subscribe({
      next: () =>
        this.avisos.update((lista) =>
          lista.map((a) => (a.id === aviso.id ? { ...a, readAt: new Date().toISOString() } : a)),
        ),
      error: () => undefined,
    });
  }
}
